/**
 * Apply rate card dialog (contracts-plan.md §8.3, WP-52; §11 tab 4). TWO-STEP,
 * exactly §8.3's "the apply dialog shows both with their basis and the person
 * picks per negotiation":
 *
 *   1. scope + therapeutic area, posted with NO `picks`.
 *   2. if the response's `disagreements` is non-empty, a radio per topic
 *      between the card's Preferred and the disagreeing position (each with
 *      its basis) — re-posts with `picks`.
 *
 * The final response (no disagreements left) calls `onApplied` immediately
 * — the Changes panel and the grid update while this dialog is still open —
 * then shows the results (`proposals`, `unmatched`, `skipped`) as stated
 * lists (G3, G9, G13: a line that reached no proposal has to say why) until
 * the person closes it.
 *
 * POST /workspaces/:id/apply/rate-card is mounted in
 * backend/src/routes/contracts/workspace.js as of commit 2bf341e3
 * (APPLY-ROUTES; see api/workspace.ts's header). `onError` still surfaces
 * any failure loudly (G13): a toast in the parent plus this dialog's own
 * inline `ws-apply-rate-card-error`.
 */
import { useEffect, useState } from 'react';
import { Modal, FilterSelect, primaryBtnCls, secondaryBtnCls } from '../../../ui';
import { listMasterEntries } from '../../api/client';
import type { MasterEntry } from '../../api/client';
import {
  applyRateCard,
  describeWorkspaceApiError,
  type ApplyRateCardResult,
  type RateCardScope,
} from '../../api/workspace';

interface SiteOption {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  ambientSiteId: string | null;
  sites: SiteOption[];
  onApplied: (result: ApplyRateCardResult) => void;
  onError: (message: string) => void;
}

type Phase = 'form' | 'disagreements' | 'results';

export function ApplyRateCardDialog({ isOpen, onClose, workspaceId, ambientSiteId, sites, onApplied, onError }: Props) {
  const [scope, setScope] = useState<RateCardScope>('site');
  const [siteId, setSiteId] = useState(ambientSiteId ?? '');
  const [therapeuticAreaId, setTherapeuticAreaId] = useState('');
  const [areas, setAreas] = useState<MasterEntry[]>([]);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>('form');
  const [response, setResponse] = useState<ApplyRateCardResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    listMasterEntries('therapeutic_area')
      .then(setAreas)
      .catch(() => setAreas([]));
  }, [isOpen]);

  if (!isOpen) return null;

  const path = `/api/contracts/workspaces/${workspaceId}/apply/rate-card`;

  async function submit(withPicks: boolean) {
    setSubmitting(true);
    setError(null);
    try {
      const result = await applyRateCard(workspaceId, {
        scope,
        siteId: scope === 'site' ? siteId || undefined : undefined,
        therapeuticAreaId: therapeuticAreaId || null,
        picks: withPicks ? picks : undefined,
      });
      setResponse(result);
      if (result.disagreements.length > 0 && !withPicks) {
        setPhase('disagreements');
      } else {
        onApplied(result);
        setPhase('results');
      }
    } catch (err) {
      const message = describeWorkspaceApiError('POST', path, err);
      setError(message);
      onError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setPhase('form');
    setResponse(null);
    setPicks({});
    setError(null);
    onClose();
  }

  // Unmatched/skipped are rendered whenever `response` is set, not only in
  // 'results': §8.3's awaiting-pick lines exist ONLY on the first
  // (disagreements) response — once the person picks and the dialog
  // re-posts, those rows are no longer skipped, so this is the only place
  // they are ever shown (WP-55 review, blocking fix 5).
  const unmatchedAndSkipped = response && (
    <>
      {response.unmatched.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Unmatched ({response.unmatched.length})
          </h4>
          <ul className="mt-1 list-inside list-disc text-sm text-gray-600 dark:text-gray-300">
            {response.unmatched.map((u) => (
              <li key={u.line_id}>
                {u.sheet}!row {u.row} &mdash; {u.label_raw}
              </li>
            ))}
          </ul>
        </div>
      )}
      {response.skipped.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Skipped ({response.skipped.length})
          </h4>
          <ul className="mt-1 list-inside list-disc text-sm text-gray-600 dark:text-gray-300">
            {response.skipped.map((s, i) => (
              <li key={`${s.line_id}-${i}`}>
                {s.sheet}!row {s.row} &mdash; {s.label_raw}: {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={close} title="Apply rate card" maxWidth="max-w-xl">
      <div data-testid="ws-apply-rate-card-dialog" className="space-y-3">
        {phase === 'form' && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <FilterSelect
                label="Scope"
                auto
                value={scope}
                onChange={(v) => setScope(v as RateCardScope)}
                options={[
                  { value: 'site', label: 'This site' },
                  { value: 'network', label: 'Company-wide' },
                ]}
              />
              {scope === 'site' && (
                <FilterSelect
                  label="Site"
                  auto
                  value={siteId}
                  onChange={setSiteId}
                  options={sites.map((s) => ({ value: s.id, label: s.name }))}
                />
              )}
              <FilterSelect
                label="Therapeutic area"
                auto
                value={therapeuticAreaId}
                onChange={setTherapeuticAreaId}
                options={[{ value: '', label: 'All areas' }, ...areas.map((a) => ({ value: a.id, label: a.official_name }))]}
              />
            </div>
            {error && (
              <p data-testid="ws-apply-rate-card-error" className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className={secondaryBtnCls} onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                data-testid="ws-apply-rate-card-submit"
                className={primaryBtnCls}
                disabled={submitting}
                onClick={() => submit(false)}
              >
                Apply
              </button>
            </div>
          </>
        )}

        {phase === 'disagreements' && response && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              The card&rsquo;s Preferred value disagrees with an active position on these topics (§8.3, §9.2) — pick which ask
              to use for this negotiation.
            </p>
            <div className="space-y-3">
              {response.disagreements.map((d) => (
                <fieldset
                  key={d.topic}
                  data-testid={`ws-arc-disagreement-${d.topic}`}
                  className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700"
                >
                  <legend className="px-1 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    {d.topic}
                  </legend>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name={`ws-pick-${d.topic}`}
                      data-testid={`ws-pick-${d.topic}-card`}
                      checked={picks[d.topic] === 'card'}
                      onChange={() => setPicks((p) => ({ ...p, [d.topic]: 'card' }))}
                    />
                    Card Preferred: {d.card.value ?? '—'}
                  </label>
                  <label className="flex items-center gap-2 text-sm" title={`Basis: ${d.position.basis} (${d.position.basis_ref})`}>
                    <input
                      type="radio"
                      name={`ws-pick-${d.topic}`}
                      data-testid={`ws-pick-${d.topic}-position`}
                      checked={picks[d.topic] === d.position.id}
                      onChange={() => setPicks((p) => ({ ...p, [d.topic]: d.position.id }))}
                    />
                    Position: {d.position.value ?? '—'} — {d.position.basis} ({d.position.basis_ref})
                  </label>
                </fieldset>
              ))}
            </div>
            {unmatchedAndSkipped}
            {error && (
              <p data-testid="ws-apply-rate-card-error" className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className={secondaryBtnCls} onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                data-testid="ws-apply-rate-card-repost"
                className={primaryBtnCls}
                disabled={submitting || response.disagreements.some((d) => !picks[d.topic])}
                onClick={() => submit(true)}
              >
                Apply with picks
              </button>
            </div>
          </>
        )}

        {phase === 'results' && response && (
          <div data-testid="ws-apply-rate-card-results" className="space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-100">
              {response.proposals.length} value{response.proposals.length === 1 ? '' : 's'} proposed.
            </p>
            {unmatchedAndSkipped}
            <div className="flex justify-end pt-2">
              <button type="button" data-testid="ws-apply-rate-card-close" className={primaryBtnCls} onClick={close}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ApplyRateCardDialog;
